import React, { useState } from "react";
import { Mail, Send, Linkedin, Github, ArrowUpRight, CheckCircle } from "lucide-react";
import { personalInfo } from "../data/mock";
import axios from "axios";

const BACKEND_URL = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });

      if (response.data.success) {
        setSubmitted(true);
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setSubmitted(false), 4000);
      }
    } catch (err) {
      console.error("Failed to submit:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32">
      {/* Top divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="max-w-6xl mx-auto px-6">
        {/* Section header */}
        <div className="mb-16">
          <p className="text-blue-400 text-sm font-medium font-mono tracking-wide mb-3">
            06 — Contact
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            Get In Touch
          </h2>
          <p className="text-zinc-500 text-base max-w-lg">
            Interested in working together? Feel free to reach out through the form below or connect directly.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.03] border border-white/8 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/40 focus:bg-blue-500/[0.02] transition-all duration-200"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.03] border border-white/8 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/40 focus:bg-blue-500/[0.02] transition-all duration-200"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell me about your project or opportunity..."
                  className="w-full px-4 py-3 rounded-lg bg-white/[0.03] border border-white/8 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/40 focus:bg-blue-500/[0.02] transition-all duration-200 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="group inline-flex items-center gap-2.5 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  "Sending..."
                ) : submitted ? (
                  <>
                    <CheckCircle size={16} />
                    Message Sent!
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </>
                )}
              </button>

              {submitted && (
                <p className="text-sm text-emerald-400 mt-2">
                  Thank you! I&apos;ll get back to you soon.
                </p>
              )}
            </form>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Direct email */}
            <div>
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">
                Direct Email
              </h3>
              <a
                href={`mailto:${personalInfo.email}`}
                className="group inline-flex items-center gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-blue-500/15 transition-all duration-300 hover:bg-blue-500/[0.02] w-full"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-500/8 border border-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/12 transition-colors duration-300">
                  <Mail size={18} className="text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {personalInfo.email}
                  </p>
                  <p className="text-xs text-zinc-600">Preferred method</p>
                </div>
              </a>
            </div>

            {/* Social links */}
            <div>
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-4">
                Connect
              </h3>
              <div className="space-y-3">
                <a
                  href={personalInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-blue-500/15 transition-all duration-300 hover:bg-blue-500/[0.02]"
                >
                  <div className="flex items-center gap-3">
                    <Linkedin size={18} className="text-zinc-500 group-hover:text-blue-400 transition-colors duration-200" />
                    <span className="text-sm font-medium text-zinc-400 group-hover:text-white transition-colors duration-200">
                      LinkedIn
                    </span>
                  </div>
                  <ArrowUpRight
                    size={14}
                    className="text-zinc-700 group-hover:text-blue-400 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>

                <a
                  href={personalInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-blue-500/15 transition-all duration-300 hover:bg-blue-500/[0.02]"
                >
                  <div className="flex items-center gap-3">
                    <Github size={18} className="text-zinc-500 group-hover:text-white transition-colors duration-200" />
                    <span className="text-sm font-medium text-zinc-400 group-hover:text-white transition-colors duration-200">
                      GitHub
                    </span>
                  </div>
                  <ArrowUpRight
                    size={14}
                    className="text-zinc-700 group-hover:text-white transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </div>
            </div>

            {/* Location */}
            <div className="p-4 rounded-xl bg-white/[0.01] border border-white/5">
              <p className="text-xs text-zinc-600 mb-1">Based in</p>
              <p className="text-sm font-medium text-zinc-400">
                {personalInfo.location}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
